import React, { useEffect, useMemo, useState } from 'react'
import {
  CButton,
  CCard,
  CCardBody,
  CCol,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CLoadingButton,
  CNav,
  CNavItem,
  CNavLink,
  CRow,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilExternalLink } from '@coreui/icons'
import ToastComponent from 'src/components/common/TaostComponent'
import { ImportErrorAlert, InfoBox } from 'src/components/common/ImportWizard'
import ClubStatsService from 'src/service/ClubStatsService'
import LinkImporter from './LinkImporter'
import FixturesImporter from './FixturesImporter'
import RoundsImporter from './RoundsImporter'
import ScoresImporter from './ScoresImporter'
import { gradeConfig, playerConfig, teamConfig } from './linkConfigs'
import { LoadingBlock, errorsOf } from './clubstatsShared'

const TABS = [
  { key: 'grades', label: 'Grades' },
  { key: 'teams', label: 'Teams' },
  { key: 'players', label: 'Players' },
  { key: 'rounds', label: 'Rounds' },
  { key: 'fixtures', label: 'Fixtures' },
  { key: 'squads', label: 'Squads' },
  { key: 'scores', label: 'Scores' },
]

const hostOf = (url) => {
  try {
    return new URL(url).host
  } catch (e) {
    return url
  }
}

/**
 * ClubStats Import - for clubs whose grades, teams, players or scorecards are not on PlayHQ. It
 * is separate from the PlayHQ Import: its own link, and its own ClubStats id on each record.
 *
 * The game links to its club's ClubStats site once, with the secret the club's admin generated
 * in ClubStats (MyClubtap Export → Generate secret). ClubStats only serves the current season,
 * so there is no season to pick; the admin imports grades, teams, players, rounds, fixtures, and
 * squads with scores - each on its own, in any order, optionally for one team.
 *
 * Grades can be deactivated on the Grades tab: the server then leaves their teams, players,
 * rounds, fixtures and squads out of the other tabs, so a club with dozens of grades only works
 * through the ones its game is about.
 */
const ClubStatsImport = () => {
  const [info, setInfo] = useState(null)
  const [loadError, setLoadError] = useState(null)

  // club link
  const [editingLink, setEditingLink] = useState(false)
  const [baseUrl, setBaseUrl] = useState('')
  const [secret, setSecret] = useState('')
  const [linkErrors, setLinkErrors] = useState([])
  const [savingLink, setSavingLink] = useState(false)

  // The club's teams this season, for the team filter. The tabs open once these are in: they
  // read the same ClubStats fixtures, and ClubStats takes one request at a time.
  const [teams, setTeams] = useState(null)
  const [teamsError, setTeamsError] = useState(null)
  const [teamFilter, setTeamFilter] = useState('')
  const [teamsVersion, setTeamsVersion] = useState(0)

  // ClubStats grade ids the admin deactivated. Kept apart from `info.link` so switching a grade
  // does not reload the teams (and remount the tab being worked in).
  const [inactiveGrades, setInactiveGrades] = useState([])

  // Each tab mounts afresh when opened, so it always reads what the other tabs just imported.
  const [tab, setTab] = useState('grades')

  const load = () => {
    setLoadError(null)
    ClubStatsService.getLink()
      .then((res) => {
        if (!res.success) {
          setLoadError(errorsOf(res)[0])
          return
        }
        setInfo(res.data)
        setInactiveGrades(res.data.link?.inactive_grade_ids || [])
      })
      .catch(() => setLoadError('This page could not be loaded. Please refresh to try again.'))
  }

  useEffect(load, [])

  const link = info?.link

  useEffect(() => {
    setTeams(null)
    setTeamsError(null)
    setTeamFilter('')
    if (!link) return
    ClubStatsService.preview('teams', { all_grades: true })
      .then((res) => {
        if (res.success) setTeams(res.data.items)
        else {
          setTeams([])
          setTeamsError(errorsOf(res)[0])
        }
      })
      .catch(() => {
        setTeams([])
        setTeamsError('ClubStats teams could not be loaded.')
      })
  }, [link, teamsVersion])

  const teamIds = useMemo(() => (teamFilter ? [teamFilter] : []), [teamFilter])

  const inactiveSet = useMemo(() => new Set(inactiveGrades), [inactiveGrades])
  const activeTeams = useMemo(
    () => (teams || []).filter((t) => !inactiveSet.has(t.grade_id)),
    [teams, inactiveSet],
  )
  // Grades this season, from the club's teams: how many the other tabs are showing.
  const gradeCounts = useMemo(() => {
    const all = new Set((teams || []).map((t) => t.grade_id))
    return { all: all.size, active: [...all].filter((id) => !inactiveSet.has(id)).length }
  }, [teams, inactiveSet])

  // A team whose grade was just deactivated can no longer be the filter.
  useEffect(() => {
    if (teamFilter && teams && !activeTeams.some((t) => t.clubstats_id === teamFilter)) {
      setTeamFilter('')
    }
  }, [teamFilter, teams, activeTeams])

  const setGradesActive = async (gradeIds, active) => {
    try {
      const res = await ClubStatsService.setGradesActive({ grade_ids: gradeIds, active })
      if (!res.success) {
        ToastComponent(errorsOf(res)[0], 'error')
        return
      }
      setInactiveGrades(res.data.link?.inactive_grade_ids || [])
      ToastComponent(res.message, 'success')
    } catch (e) {
      ToastComponent('The grade could not be updated. Please try again.', 'error')
    }
  }

  const startEditing = () => {
    setBaseUrl(link?.base_url || '')
    setSecret('')
    setLinkErrors([])
    setEditingLink(true)
  }

  const saveLink = async () => {
    setSavingLink(true)
    setLinkErrors([])
    try {
      const res = await ClubStatsService.saveLink({
        base_url: baseUrl.trim(),
        secret: secret.trim(),
      })
      if (!res.success) {
        setLinkErrors(errorsOf(res))
        return
      }
      ToastComponent(res.message, 'success')
      setEditingLink(false)
      setBaseUrl('')
      setSecret('')
      setInfo((current) => ({ ...current, link: res.data.link }))
      setInactiveGrades(res.data.link?.inactive_grade_ids || [])
    } catch (e) {
      setLinkErrors(['The club could not be linked. Please try again.'])
    } finally {
      setSavingLink(false)
    }
  }

  const removeLink = async () => {
    if (
      !window.confirm(
        'Remove the ClubStats link? Records you already imported stay as they are, and the secret is deleted from this game.',
      )
    )
      return
    const res = await ClubStatsService.removeLink()
    if (res.success) {
      ToastComponent(res.message, 'success')
      setInfo((current) => ({ ...current, link: null }))
    }
  }

  const header = (
    <div className="mb-3">
      <h4 className="fw-bold mb-0">ClubStats Import</h4>
      <div className="small text-medium-emphasis">
        Pull grades, teams, players, rounds, fixtures, squads and scores from your club&rsquo;s
        ClubStats site - for data that is not on PlayHQ.
      </div>
    </div>
  )

  if (loadError) {
    return (
      <>
        {header}
        <ImportErrorAlert errors={[loadError]} title="Something went wrong:" />
      </>
    )
  }
  if (!info) return <LoadingBlock text="Loading…" />

  if (!info.supported) {
    return (
      <>
        {header}
        <InfoBox color="light">ClubStats import is available for club games.</InfoBox>
      </>
    )
  }

  const linkForm = (
    <CCard className="mb-4 border-0 shadow-sm">
      <CCardBody>
        <h5 className="fw-semibold mb-1">
          {link ? 'Change your ClubStats link' : 'Link your ClubStats club'}
        </h5>
        <div className="small text-medium-emphasis mb-3">
          Your club&rsquo;s ClubStats admin opens <strong>MyClubtap Export</strong> in ClubStats,
          clicks <strong>Generate secret</strong>, and shares the secret with you. Paste it here
          with your club&rsquo;s ClubStats address. You only need to do this once - but generating a
          new secret in ClubStats stops the old one straight away, so update it here when that
          happens.
        </div>
        <CRow className="g-3">
          <CCol md={6}>
            <CFormLabel className="small fw-semibold">ClubStats address</CFormLabel>
            <CFormInput
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder="https://yourclub.clubstats.cricket"
            />
          </CCol>
          <CCol md={6}>
            <CFormLabel className="small fw-semibold">Secret</CFormLabel>
            <CFormInput
              type="password"
              autoComplete="off"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              placeholder={
                link ? `Leave empty to keep the current one (${link.secret_hint || '…'})` : ''
              }
              onKeyDown={(e) => e.key === 'Enter' && baseUrl.trim() && saveLink()}
            />
          </CCol>
        </CRow>
        <div className="d-flex gap-2 mt-3">
          <CLoadingButton
            color="primary"
            loading={savingLink}
            disabled={!baseUrl.trim() || (!link && !secret.trim())}
            onClick={saveLink}
          >
            {link ? 'Save link' : 'Link club'}
          </CLoadingButton>
          {link && (
            <CButton color="secondary" variant="outline" onClick={() => setEditingLink(false)}>
              Cancel
            </CButton>
          )}
        </div>
        <div className="mt-3">
          <ImportErrorAlert errors={linkErrors} title="The club could not be linked:" />
        </div>
      </CCardBody>
    </CCard>
  )

  if (!link || editingLink) {
    return (
      <>
        {header}
        {linkForm}
      </>
    )
  }

  const selectedTeam = (teams || []).find((t) => t.clubstats_id === teamFilter)

  return (
    <>
      {header}
      <CCard className="mb-3 border-0 shadow-sm">
        <CCardBody>
          <CRow className="g-3 align-items-end">
            <CCol lg={5}>
              <div className="fw-semibold">{hostOf(link.base_url)}</div>
              <div className="small text-medium-emphasis">
                {link.season_label ? `${link.season_label} · ` : ''}Secret {link.secret_hint}
              </div>
              <a href={link.base_url} target="_blank" rel="noreferrer" className="small">
                Open ClubStats <CIcon icon={cilExternalLink} size="sm" />
              </a>
            </CCol>
            <CCol lg={4}>
              <CFormLabel className="small fw-semibold">Team</CFormLabel>
              <CFormSelect
                value={teamFilter}
                onChange={(e) => setTeamFilter(e.target.value)}
                disabled={!teams || activeTeams.length < 2}
              >
                <option value="">{teams ? 'All teams' : 'Loading teams…'}</option>
                {activeTeams.map((t) => (
                  <option key={t.clubstats_id} value={t.clubstats_id}>
                    {t.name} ({t.grade_name})
                  </option>
                ))}
              </CFormSelect>
            </CCol>
            <CCol lg={3} className="d-flex gap-2 justify-content-lg-end">
              <CButton size="sm" color="secondary" variant="outline" onClick={startEditing}>
                Change link
              </CButton>
              <CButton size="sm" color="danger" variant="ghost" onClick={removeLink}>
                Remove link
              </CButton>
            </CCol>
          </CRow>
          {teamsError && (
            <div className="mt-3">
              <ImportErrorAlert errors={[teamsError]} title="ClubStats could not be read:" />
              <CButton
                size="sm"
                color="primary"
                variant="outline"
                onClick={() => setTeamsVersion((v) => v + 1)}
              >
                Try again
              </CButton>
            </div>
          )}
        </CCardBody>
      </CCard>

      <CCard className="mb-4 border-0 shadow-sm">
        <CCardBody>
          <CNav variant="underline" className="mb-4 flex-nowrap overflow-auto">
            {TABS.map((t) => (
              <CNavItem key={t.key}>
                <CNavLink
                  active={tab === t.key}
                  onClick={() => setTab(t.key)}
                  style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                >
                  {t.label}
                </CNavLink>
              </CNavItem>
            ))}
          </CNav>
          {tab !== 'grades' && teams && gradeCounts.active < gradeCounts.all && (
            <GradesNotice counts={gradeCounts} tab={tab} onManage={() => setTab('grades')} />
          )}
          {selectedTeam && tab !== 'grades' && tab !== 'teams' && (
            <div className="small text-medium-emphasis mb-3">Showing {selectedTeam.name} only.</div>
          )}
          {!teams ? (
            <LoadingBlock text="Loading your club’s season from ClubStats…" />
          ) : (
            <Importer
              key={CLUB_WIDE.includes(tab) ? tab : `${tab}:${teamFilter}`}
              tab={tab}
              teamIds={teamIds}
              activation={{ inactive: inactiveSet, onChange: setGradesActive }}
            />
          )}
        </CCardBody>
      </CCard>
    </>
  )
}

// Tells the admin the other tabs are narrowed to the active grades.
const GradesNotice = ({ counts, tab, onManage }) => (
  <div
    className={`small mb-3 ${counts.active ? 'text-medium-emphasis' : 'text-warning fw-semibold'}`}
  >
    {counts.active
      ? `Showing ${counts.active} of ${counts.all} grades - deactivated grades are left out.`
      : 'Every grade is deactivated, so ClubStats has nothing to show here.'}
    {tab === 'players' &&
      counts.active > 0 &&
      ' Players with no team in ClubStats are always shown.'}{' '}
    <CButton color="link" size="sm" className="p-0 align-baseline" onClick={onManage}>
      Manage grades
    </CButton>
  </div>
)

// Grades and teams are about the whole club, not one team, so the team filter does not apply.
const CLUB_WIDE = ['grades', 'teams']
const NO_TEAMS = []
const Importer = ({ tab, teamIds, activation }) => {
  if (tab === 'grades')
    return (
      <LinkImporter type="grades" teamIds={NO_TEAMS} config={gradeConfig} activation={activation} />
    )
  if (tab === 'teams') return <LinkImporter type="teams" teamIds={NO_TEAMS} config={teamConfig} />
  if (tab === 'players')
    return <LinkImporter type="players" teamIds={teamIds} config={playerConfig} />
  if (tab === 'rounds') return <RoundsImporter teamIds={teamIds} />
  if (tab === 'fixtures') return <FixturesImporter teamIds={teamIds} />
  if (tab === 'squads') return <ScoresImporter teamIds={teamIds} />
  return <ScoresImporter teamIds={teamIds} withScores />
}

export default ClubStatsImport
